import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Share2, Mail } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REF_KEY = "asya_ref_code";

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {referral_code, referrals, position, total, already}
  const [copied, setCopied] = useState(false);

  const incomingRef = new URLSearchParams(window.location.search).get("ref") || null;

  const refreshStatus = useCallback(async (code) => {
    try {
      const r = await axios.get(`${API}/waitlist/status`, { params: { code } });
      setStatus((s) => ({ ...(s || {}), ...r.data }));
    } catch (_) {}
  }, []);

  // Returning visitor: restore their status from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(REF_KEY);
    if (saved) refreshStatus(saved);
  }, [refreshStatus]);

  const referralLink = status
    ? `${window.location.origin}/?ref=${status.referral_code}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/waitlist`, {
        email,
        name: name || null,
        ref: incomingRef,
      });
      setStatus(r.data);
      localStorage.setItem(REF_KEY, r.data.referral_code);
      toast.success(r.data.already ? "You're already on the list." : "You're on the list.", {
        description: `You're #${r.data.position} in line.`,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Couldn't copy — please copy it manually.");
    }
  };

  const shareX = () => {
    const text = encodeURIComponent("I just joined the ASYA early-access list — personalized skincare, delivered. Join me:");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralLink)}`, "_blank");
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("Join me on the ASYA waitlist");
    const body = encodeURIComponent(`I'm on the early-access list for ASYA. Join with my link and we both move up:\n\n${referralLink}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <section id="waitlist" className="relative py-28 md:py-44 scroll-mt-24 overflow-hidden">
      <div className="pointer-events-none absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] halo" />
      <div className="relative mx-auto max-w-[1000px] px-6 md:px-10 text-center">
        <Reveal>
          <p className="overline mb-6">Early access</p>
          <h2 className="font-serif font-light text-5xl md:text-8xl leading-[0.92] tracking-[-0.02em] text-balance">
            Be first to see<br />your skin, understood.
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          {status ? (
            <div data-testid="waitlist-success" className="mt-14 mx-auto max-w-xl rounded-2xl border border-primary/40 bg-card p-8 md:p-10 text-left">
              <div className="flex items-baseline justify-between mb-1">
                <p className="font-serif text-3xl">You're in.</p>
                <span data-testid="queue-position" className="font-mono text-sm text-primary">#{status.position} in line</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Joined by {status.total.toLocaleString()} people. Invite friends with your link — every friend who joins moves you up the queue.
              </p>

              <div className="mt-8">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your referral link</label>
                <div className="mt-3 flex items-center gap-3">
                  <input
                    data-testid="referral-link"
                    readOnly
                    value={referralLink}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 bg-transparent border-b border-white/20 py-2.5 text-sm outline-none"
                  />
                  <button
                    data-testid="copy-referral-button"
                    onClick={copyLink}
                    className="shrink-0 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2.5 text-accent-foreground text-[11px] font-mono uppercase tracking-[0.18em] hover:bg-white transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button data-testid="share-x-button" onClick={shareX} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-white/50 transition-colors">
                  <Share2 className="w-3.5 h-3.5" /> Share on X
                </button>
                <button data-testid="share-email-button" onClick={shareEmail} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs hover:border-white/50 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Email a friend
                </button>
                <span data-testid="referral-count" className="ml-auto font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {status.referrals} referred · move up
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-14 mx-auto max-w-xl text-left" data-testid="waitlist-form">
              {incomingRef && (
                <p className="mb-6 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
                  A friend invited you — you'll both move up the queue
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">First name</label>
                  <input
                    data-testid="waitlist-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Optional"
                    className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-primary outline-none py-3 text-lg placeholder:text-muted-foreground/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email address</label>
                  <input
                    data-testid="waitlist-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-primary outline-none py-3 text-lg placeholder:text-muted-foreground/50 transition-colors"
                  />
                </div>
              </div>
              <button
                data-testid="waitlist-submit-button"
                type="submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-accent px-8 py-4 text-accent-foreground text-sm font-medium hover:bg-white transition-all duration-300 disabled:opacity-60"
              >
                {loading ? "Joining…" : "Join the waitlist"}
                {!loading && <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>}
              </button>
              <p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Refer friends after joining to jump the queue
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
